import { SIM, DEBUG } from '../config/tuning';
import { Time } from './Time';
import { bus } from './EventBus';

/**
 * Fixed-timestep loop with interpolated rendering — SPEC §4.3, implemented exactly:
 *
 *   accumulator += min(realDelta, MAX_FRAME_DT) * timeScale
 *   while (accumulator >= FIXED_DT) { simulate(FIXED_DT); accumulator -= FIXED_DT }
 *   alpha = accumulator / FIXED_DT
 *   render(alpha)
 *
 * Two properties this buys, both of which are effectively impossible to retrofit:
 *  - The simulation never sees a variable dt, so a run is a pure function of (seed, inputs).
 *  - Rendering interpolates, so 120 Hz simulation on a 60 Hz display has no judder. Without it,
 *    the beat frequency between tick and frame reads as a physics bug.
 *
 * The 0.25 s clamp is what stops a tab-switch stall from turning into a several-second
 * catch-up burst (and, with it, a spiral of death): it bounds a frame to 30 ticks.
 */

export interface LoopHandlers {
  /** One deterministic simulation tick. dtS is always exactly Time.fixedDtS, seconds. */
  fixedUpdate(dtS: number, time: Time): void;
  /** Draw one frame, interpolating state by alpha ∈ [0,1) between the last two ticks. */
  render(alpha: number, time: Time): void;
}

export class Loop {
  readonly time: Time;

  private readonly handlers: LoopHandlers;
  private readonly maxFrameDtS: number;

  /** Unsimulated real time carried between frames, seconds. */
  private accumulatorS = 0;

  /** Ticks queued by explicit single-step requests; they bypass the pause gate. */
  private pendingSteps = 0;

  private running = false;
  private rafHandle = 0;
  private lastFrameMs = 0;
  private timeScaleIndex = 0;

  // --- Frame statistics, wall-clock milliseconds. Debug display only; never read by the sim.
  /** Wall time between the last two frames. */
  frameMs = 0;
  /** Time spent inside fixedUpdate this frame. */
  simMs = 0;
  /** Time spent inside render this frame. */
  renderMs = 0;
  /** Ticks executed during the last frame. */
  ticksLastFrame = 0;

  constructor(handlers: LoopHandlers, time: Time = new Time(SIM.FIXED_DT)) {
    this.handlers = handlers;
    this.time = time;
    this.maxFrameDtS = SIM.MAX_FRAME_DT;
  }

  /** Interpolation factor for the current frame, [0, 1). */
  get alpha(): number {
    return this.time.alpha;
  }

  /** Unsimulated remainder, seconds. Exposed for the debug overlay. */
  get accumulator(): number {
    return this.accumulatorS;
  }

  get isRunning(): boolean {
    return this.running;
  }

  get isPaused(): boolean {
    return this.time.paused;
  }

  get timeScale(): number {
    return this.time.timeScale;
  }

  /**
   * Advance the simulation by one frame's worth of real time. Returns the number of ticks run.
   *
   * This is the whole loop minus rendering and the wall clock, which is what makes headless
   * determinism tests possible: feed it any dt sequence and the tick sequence is identical.
   */
  advance(realDtS: number): number {
    let ticks = 0;

    // Single-step overrides the pause gate and deliberately leaves the accumulator alone, so
    // stepping does not shift the render interpolation phase.
    if (this.pendingSteps > 0) {
      while (this.pendingSteps > 0) {
        this.runTick();
        this.pendingSteps -= 1;
        ticks += 1;
      }
      this.ticksLastFrame = ticks;
      bus.emit('time:stepped', { tick: this.time.tick });
      return ticks;
    }

    if (this.time.paused) {
      this.ticksLastFrame = 0;
      return 0;
    }

    const clampedDtS = realDtS < this.maxFrameDtS ? realDtS : this.maxFrameDtS;
    this.accumulatorS += clampedDtS * this.time.timeScale;

    const fixedDtS = this.time.fixedDtS;
    while (this.accumulatorS >= fixedDtS) {
      this.runTick();
      this.accumulatorS -= fixedDtS;
      ticks += 1;
    }

    this.time.alpha = this.accumulatorS / fixedDtS;
    this.ticksLastFrame = ticks;
    return ticks;
  }

  /** Start the requestAnimationFrame driver. */
  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastFrameMs = 0;
    this.rafHandle = requestAnimationFrame(this.onAnimationFrame);
  }

  /** Stop the driver. The simulation state is untouched. */
  stop(): void {
    if (!this.running) return;
    this.running = false;
    cancelAnimationFrame(this.rafHandle);
    this.rafHandle = 0;
  }

  /** Freeze or resume time. Rendering continues, at the frozen alpha. */
  setPaused(paused: boolean): void {
    if (this.time.paused === paused) return;
    this.time.paused = paused;
    bus.emit('time:paused', { paused });
  }

  togglePause(): void {
    this.setPaused(!this.time.paused);
  }

  /**
   * Queue exactly n ticks and pause. Stepping through a terminal intercept one tick at a time
   * is the only practical way to debug a proximity fuse (SPEC §4.6).
   */
  step(ticks = 1): void {
    this.setPaused(true);
    this.pendingSteps += ticks;
  }

  /** Set the wall-clock multiplier: 1 = real time, 0.1 = the slow-motion debug setting. */
  setTimeScale(scale: number): void {
    if (this.time.timeScale === scale) return;
    this.time.timeScale = scale;
    bus.emit('time:scale', { scale });
  }

  /** Cycle through the debug time scales (1×, 0.5×, 0.25×, 0.1×). */
  cycleTimeScale(): void {
    this.timeScaleIndex = (this.timeScaleIndex + 1) % DEBUG.TIME_SCALES.length;
    this.setTimeScale(DEBUG.TIME_SCALES[this.timeScaleIndex]);
  }

  /** Reset the clock and drop the unsimulated remainder. Does not reset world state. */
  resetClock(): void {
    this.time.reset();
    this.accumulatorS = 0;
    this.pendingSteps = 0;
  }

  private runTick(): void {
    this.handlers.fixedUpdate(this.time.fixedDtS, this.time);
    this.time.advanceTick();
  }

  /** Bound once at construction — a per-frame closure would allocate. */
  private readonly onAnimationFrame = (nowMs: number): void => {
    if (!this.running) return;
    this.rafHandle = requestAnimationFrame(this.onAnimationFrame);

    // The first frame has no previous timestamp; a zero-length frame is the honest value.
    if (this.lastFrameMs === 0) this.lastFrameMs = nowMs;
    this.frameMs = nowMs - this.lastFrameMs;
    this.lastFrameMs = nowMs;

    const simStartMs = performance.now();
    this.advance(this.frameMs / 1000);
    const renderStartMs = performance.now();
    this.handlers.render(this.time.alpha, this.time);
    const frameEndMs = performance.now();

    this.simMs = renderStartMs - simStartMs;
    this.renderMs = frameEndMs - renderStartMs;
  };
}
